import { FC, useEffect, useState } from 'react';
import {
  FAIcon,
  IconStyle,
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  TooltipPreference,
  useTranslation,
} from '@nextgen-web-framework/all';
import { Field, FieldProps, useFormik, FormikProvider, Form } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import { PersonalIdRequestTypeEnum, updateRegistrationData } from '@commerce-profile-webcomponents/sdk';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { TextConstants } from '../../utils/Constants';
import styles from './RegistrationCIDVerification.module.scss';
import { RegistrationCIDVerificationProps } from './types/propTypes';

const RegistrationCIDVerification: FC<RegistrationCIDVerificationProps> = ({
  idType,
  label,
  callback,
  message,
  disabled,
  name,
  isABO,
  isUser,
  disableVerify,
  updateDisableVerify,
  validationSchema,
}) => {
  const t = useTranslation();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const [lastcid, setLastcid] = useState('');
  const { cidVerification } = registrationFlowData;
  const { isRadioChanged } = cidVerification;
  const [showLoading, setShowLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const updateSlice = (val: boolean) => {
    const updatedState = isUser ? { isUserCidVerified: val } : { isSpouseVerified: val };
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        cidVerification: {
          ...cidVerification,
          ...updatedState,
        },
      }),
    );
  };
  const formik = useFormik({
    initialValues: { cid: '', passport: '' },
    // eslint-disable-next-line
    onSubmit: () => {},
    validationSchema: validationSchema,
    validateOnBlur: true,
  });

  const verifyCTAClassName = (value: string, error?: string) =>
    `${!value || error || disableVerify ? styles['verify-link-disabled'] : styles['verify-link']}`;

  const onIDVerifyClick = (idTypeName: string, value?: string, error?: string) => {
    if (error) {
      return;
    }
    setLastcid(value || '');
    value && callback(idTypeName, value, setErrors, setShowLoading);
  };

  const setErrors = (name: string, message: string) => {
    formik.setFieldError(name, message);
  };
  const idLabel = t(
    idType === PersonalIdRequestTypeEnum.Cid
      ? 'profile.RegistrationCIDVerification.CIDLabel'
      : 'profile.RegistrationCIDVerification.passport',
  );

  const { handleChange, handleBlur, setFieldValue } = formik;
  useEffect(() => {
    if (isABO) {
      setFieldValue('passport', '');
    } else {
      setFieldValue('cid', '');
      setFieldValue('passport', '');
    }
  }, [isRadioChanged]);

  const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value !== lastcid) {
      handleBlur(event);
    }
  };

  return (
    <FormikProvider value={formik}>
      <Form>
        <div className={styles['input-cid-number-field']}>
          <Field type="text" name={name}>
            {({ meta }: FieldProps) => (
              <>
                <Input
                  id={name}
                  data-testid={name}
                  type={InputTypes.TEXT}
                  name={name}
                  value={meta.value}
                  label={t(`${label}`)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (showLoading) {
                      return;
                    }
                    handleChange(e);
                    e.target.value.length === 0 ? updateDisableVerify(true) : updateDisableVerify(false);

                    updateSlice(false);
                  }}
                  onBlur={(e) => onBlurHandler(e)}
                  mlpVersion={MLPVersion.TWO}
                  disabled={disabled}
                  status={inputErrorStatus(meta.touched, meta.error)}
                  message={inputErrorMessage(t, meta.touched, meta.error, {
                    max: TextConstants.CID_MAX,
                  })}
                  className={verifyCTAClassName(meta.value, meta.error)}
                  ctaTextLink={{
                    text: t('profile.SinglePageRegistrationStep.verify'),
                    id: 'btn_verify',
                    hidden: showLoading,
                    //pass formik object to onVerify
                    onClick: () => onIDVerifyClick(name, meta.value, meta.error),
                    disabled: disableVerify,
                  }}
                  labelInfo={t(
                    idType === PersonalIdRequestTypeEnum.Cid
                      ? 'profile.RegistrationCIDVerification.CIDTooltip'
                      : 'profile.RegistrationCIDVerification.passportTooltip',
                  )}
                  labelInfoPreference={TooltipPreference.TOP_LEFT}
                  inputIcon={showLoading ? { iconName: 'spinner-third', iconStyle: IconStyle.SOLID } : undefined}
                  iconClassName="fa-spin"
                />
                {message && !meta.error && (
                  <span
                    role="presentation"
                    aria-label="message"
                    className={`${styles['float-input-message']}`}
                    data-testid="message-test-id"
                  >
                    {<FAIcon iconName={'circle-check'} theme={Theme.GREEN} className={styles['input-icon']} />}
                    {<span className={styles['float-message']}>{t(`${message}`, { idType: idLabel })} </span>}
                  </span>
                )}
              </>
            )}
          </Field>
        </div>
      </Form>
    </FormikProvider>
  );
};
export default RegistrationCIDVerification;
